import { useState } from "react";
import PropTypes from "prop-types";
import { Card, Divider, Grid, IconButton, Snackbar, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { parseISO, format as formatDate } from "date-fns";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

const WEBHOOK_FIELDS = [
  ["Orders", "ordersWebhookURL"],
  ["Sync products", "syncProductsURL"],
  ["Sync tables", "syncTablesURL"],
  ["Sync floors", "syncFloorsURL"],
  ["Operations", "operationsWebhookURL"],
  ["Store status", "storeStatusWebhookURL"],
];

const safeDate = (date) => {
  if (!date) return "—";
  try {
    return formatDate(parseISO(date), "d MMM yyyy, HH:mm");
  } catch {
    return String(date);
  }
};

const platformColor = (platform) => {
  switch (String(platform || "").toLowerCase()) {
    case "resto":
      return "info";
    case "retail":
      return "success";
    default:
      return "secondary";
  }
};

function Field({ label, children }) {
  return (
    <MDBox>
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 0.5 }}
      >
        {label}
      </MDTypography>
      {children}
    </MDBox>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function Value({ children, mono }) {
  return (
    <MDTypography
      variant="body2"
      color="dark"
      sx={{
        fontFamily: mono ? MONO : undefined,
        fontSize: mono ? 13 : 14,
        wordBreak: "break-all",
      }}
    >
      {children}
    </MDTypography>
  );
}

Value.propTypes = {
  children: PropTypes.node.isRequired,
  mono: PropTypes.bool,
};

Value.defaultProps = { mono: false };

export default function MerchantDetailCard({ merchant, total }) {
  const [revealClientId, setRevealClientId] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!merchant) return null;

  const clientId = merchant.clientId || "";
  const shownClientId = clientId
    ? revealClientId
      ? clientId
      : `${clientId.slice(0, 8)}${"•".repeat(12)}${clientId.slice(-4)}`
    : "—";

  const copyClientId = async () => {
    try {
      await navigator.clipboard.writeText(clientId);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card>
      <MDBox px={3} pt={2.5} pb={2}>
        <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <MDTypography variant="h5" color="dark" fontWeight="medium">
            {merchant.merchantName || "Unnamed merchant"}
          </MDTypography>
          {merchant.platform && (
            <MDBadge
              badgeContent={merchant.platform}
              color={platformColor(merchant.platform)}
              variant="contained"
              size="xs"
              container
            />
          )}
        </MDBox>
        {Number.isFinite(total) && (
          <MDTypography variant="button" color="text" sx={{ textTransform: "none" }}>
            One of {total} registered {total === 1 ? "merchant" : "merchants"}
          </MDTypography>
        )}
      </MDBox>

      <Divider sx={{ m: 0 }} />

      <MDBox px={3} py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <Field label="Internal merchant ID">
              <Value mono>{merchant.internalMerchantId || "—"}</Value>
            </Field>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Field label="Client ID">
              <MDBox display="flex" alignItems="center" gap={0.5}>
                <Value mono>{shownClientId}</Value>
                {clientId && (
                  <MDBox display="flex" flexShrink={0}>
                    <Tooltip title={revealClientId ? "Hide client ID" : "Reveal client ID"}>
                      <IconButton
                        size="small"
                        onClick={() => setRevealClientId((s) => !s)}
                        aria-label={revealClientId ? "Hide client ID" : "Reveal client ID"}
                      >
                        {revealClientId ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy client ID">
                      <IconButton size="small" onClick={copyClientId} aria-label="Copy client ID">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                )}
              </MDBox>
            </Field>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Field label="Registered">
              <Value>{safeDate(merchant.createdAt)}</Value>
            </Field>
          </Grid>
        </Grid>
      </MDBox>

      <Divider sx={{ m: 0 }} />

      <MDBox px={3} py={3}>
        <MDTypography variant="h6" color="dark" fontWeight="medium" mb={0.5}>
          Webhook endpoints
        </MDTypography>
        <MDTypography variant="button" color="text" sx={{ textTransform: "none", display: "block", mb: 2.5 }}>
          Where the middleware forwards Deliverect traffic for this merchant.
        </MDTypography>

        <Grid container spacing={3}>
          {WEBHOOK_FIELDS.map(([label, key]) => (
            <Grid key={key} item xs={12} md={6}>
              <Field label={label}>
                {merchant[key] ? (
                  <Value mono>{merchant[key]}</Value>
                ) : (
                  <MDTypography variant="body2" color="text" sx={{ fontSize: 14 }}>
                    Not configured
                  </MDTypography>
                )}
              </Field>
            </Grid>
          ))}
        </Grid>
      </MDBox>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Client ID copied"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Card>
  );
}

MerchantDetailCard.propTypes = {
  merchant: PropTypes.shape({
    clientId: PropTypes.string,
    merchantName: PropTypes.string,
    internalMerchantId: PropTypes.string,
    platform: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    ordersWebhookURL: PropTypes.string,
    syncProductsURL: PropTypes.string,
    syncTablesURL: PropTypes.string,
    syncFloorsURL: PropTypes.string,
    operationsWebhookURL: PropTypes.string,
    storeStatusWebhookURL: PropTypes.string,
  }),
  total: PropTypes.number,
};

MerchantDetailCard.defaultProps = {
  merchant: null,
  total: undefined,
};
